import * as React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {
  ClickAwayListener,
  Typography,
  Popover,
  FormControl,
  InputAdornment,
  Input,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  ListItemButton,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";
import { useTheme } from "../../ThemeContext/ThemeContext.js";
import {
  BodyLarge,
  BodySmall,
  H6HeadlineSmall,
  H7TitleLarge,
  H8TitleMedium,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import {
  OutlinedButton,
  SecondaryButton,
  StyledIconButton,
} from "../../components/StyledComponents/Buttons/AuradineButtons";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useState } from "react";
import { useGetSiteLists } from "../../api/queries/useGetSiteLists.ts";
import { StyledFormControl } from "../../components/StyledComponents/Inputs/Inputs.js";
import { useQueryClient } from "@tanstack/react-query";
import { StyledOutlinedInput } from "../../components/StyledComponents/Inputs/Inputs.js";

const StyledPopoverContent = styled(Box)(({ colors }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: "1rem",
  boxShadow: "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
}));

const StyledPopover = styled(Popover,)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem", 
    backgroundColor: colors?.background?.popover,
  },
}));

const StyledHeaderAndFooterBox = styled(Box)`
  padding: 1.5rem;
`;

const GroupOperationsCard = (props) => {
  const {
    open,
    handleClose,
    anchorRef,
    selectedSite,
    selectedGroup,
    setSelectedSite,
    setSelectedGroup,
    user,
  } = props;
  const queryClient = useQueryClient();
  const { colors } = useTheme();

  const history = useHistory();

  const [siteAndGroups, setSiteAndGroups] = React.useState();

  //fetching to get userPreferences to set the tableGroup and detailGroup from user object. If not present, we are setting default values
  const [authToken, setAuthToken] = React.useState("");

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  React.useEffect(() => {
    console.log(1);
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  const {
    data: siteLists,
    isLoading: isSiteGroupDataLoading,
    isFetching: isSiteGroupDataFetching,
    isFetched: isSiteGroupDataFetched,
    refetch: siteListRefetch,
  } = useGetSiteLists({
    authToken,
    leftPanelOpen: true,
  });

  React.useEffect(() => {
    if (siteLists) {
      if (siteLists?.status === "ok" && siteLists?.siteList?.length > 0) {
        const siteList = siteLists?.siteList;
        setSiteAndGroups(siteList);
        // if (user.role === "superadmin") {
        //   setSelectedSite(`Site ${user?.org_id}`);
        //   setSelectedGroup("");
        // } else {
        //   setSelectedSite(siteList[0]?.siteName);
        //   setSelectedGroup("");
        // }
      }
    }
  }, [siteLists, isSiteGroupDataFetching]);

  const [searchValue, setSearchValue] = React.useState("");

  const onSitesAndGroupSearch = (e) => {
    console.log(e.target.value);
    if (e.target.value === "") {
      setSearchValue("");
      setSiteAndGroups(siteLists?.siteList);
      return;
    } else {
      setSearchValue(e.target.value);
      const sitesData = [...siteAndGroups];
      const foundSite = sitesData.filter((site) => {
        return (
          site?.siteName.toLowerCase().includes(e.target.value.toLowerCase()) ||
          (site.groups &&
            site.groups.some((group) =>
              group.DGName.toLowerCase().includes(e.target.value.toLowerCase())
            ))
        );
        console.log("foundSite", foundSite);
      });

      setSiteAndGroups(foundSite);
    }
  };

  const inputRef = React.useRef("");
  const handleClearSearch = () => {
    inputRef.current.value = "";
    setSearchValue("");
    setSiteAndGroups(siteLists?.siteList);
  };
  return (
    <StyledPopover
      colors={colors}
      sx={{
        zIndex: 9999,
        borderRadius: "1rem",
        padding: "2rem",
      }}
      open={open}
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <StyledPopoverContent colors={colors}>
          <Box
            display="flex"
            flexDirection={"column"}
            padding="0.5rem"
            className={"search-box"}
          >
            {/* Search Input */}
            <StyledFormControl colors={colors} fullWidth sx={{ mb: 4 }} variant="standard">
              <StyledOutlinedInput
                colors={colors}
                ref={inputRef}
                id="standard-adornment-amount"
                placeholder="Search for sites or groups"
                value={searchValue}
                onChange={(event) => onSitesAndGroupSearch(event)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: colors.text.muted }} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <StyledIconButton
                      colors={colors}
                      onClick={() => {
                        handleClearSearch();
                      }}
                    >
                      <CloseIcon />
                    </StyledIconButton>
                  </InputAdornment>
                }
              />
            </StyledFormControl>

            {/* Groups List */}
            <Box
              display={"flex"}
              flexDirection={"column"}
              width={"22.5rem"}
              minWidth={"22.5rem"}
              maxHeight={"15rem"}
              minHeight={"15rem"}
              maxWidth={"45rem"}
              overflow={"auto"}
            >
              {siteAndGroups &&
                Array.isArray(siteAndGroups) &&
                siteAndGroups.length > 0 &&
                siteAndGroups.map((site, siteIndex) => (
                  <React.Fragment key={siteIndex}>
                    <ListItemButton
                      selected={
                        site.siteName === selectedSite && selectedGroup === ""
                      }
                      key={siteIndex}
                      onClick={() => {
                        setSelectedSite(site.siteName);
                        setSelectedGroup("");
                        handleClose();
                      }}
                      sx={{
                        minHeight: "3rem",
                        maxHeight: "3rem",
                        "&:hover": {
                          backgroundColor: colors.hover.muted,
                          borderRadius: "6.5rem",
                          cursor: "pointer",
                        },
                        "&.Mui-selected": {
                          backgroundColor: colors.hover.primary,
                          borderRadius: "6.5rem",
                        },
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <H8TitleMedium key={siteIndex}>
                        {site.siteName}
                      </H8TitleMedium>
                      {selectedSite &&
                        selectedSite === site.siteName &&
                        !selectedGroup && (
                          <CloseIcon
                            fontSize="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSite("");
                              setSelectedGroup("");
                            }}
                            style={{ cursor: "pointer" }} sx={{ color: colors?.text?.muted }} 
                          />
                        )}
                    </ListItemButton>

                    {site.groups &&
                      site.groups.length > 0 &&
                      site.groups.map((group, groupIndex) => (
                        <React.Fragment key={groupIndex}>
                          <ListItemButton
                            selected={
                              group.DGName === selectedGroup &&
                              site.siteName === selectedSite
                            }
                            key={groupIndex}
                            onClick={() => {
                              setSelectedSite(site.siteName);
                              setSelectedGroup(group.DGName);
                              handleClose();
                            }}
                            sx={{
                              paddingLeft: "2.5rem",
                              minHeight: "3rem",
                              maxHeight: "3rem",
                              "&:hover": {
                                backgroundColor: colors.hover.muted,
                                borderRadius: "6.5rem",
                                cursor: "pointer",
                              },
                              "&.Mui-selected": {
                                backgroundColor: colors.hover.primary,
                                borderRadius: "6.5rem",
                              },
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <H8TitleMedium
                              key={groupIndex}
                              style={{ fontWeight: "400" }}
                            >
                              {group.DGName}
                            </H8TitleMedium>

                            {selectedSite &&
                              selectedGroup &&
                              selectedSite === site.siteName &&
                              selectedGroup === group.DGName && (
                                <CloseIcon
                                  fontSize="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSite("");
                                    setSelectedGroup("");
                                  }}
                                  style={{ cursor: "pointer" }}
                                  sx={{ color: colors?.text?.muted }}
                                />
                              )}
                          </ListItemButton>
                        </React.Fragment>
                      ))}
                  </React.Fragment>
                ))}
            </Box>
          </Box>
          <Box></Box>
        </StyledPopoverContent>
      </ClickAwayListener>
    </StyledPopover>
  );
};

export default GroupOperationsCard;

export const GroupOperationsTab = (props) => {
  const {
    selectedSite,
    selectedGroup,
    setSelectedSite,
    setSelectedGroup,
    user,
    siteLists,
    isDashboardDataFetching,
    isDashboardDataLoading,
    isDashboardDataRefetching,
  } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { colors } = useTheme();

  const anchorRef = React.useRef(null);

  const [open, setOpen] = React.useState(false);
  const navigate = useHistory();
  const handleClick = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const siteAndGroups = siteLists ? siteLists?.siteList : [];
  const sites = siteAndGroups
    ? siteAndGroups?.map((site) => site.siteName)
    : [];

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          // paddingRight: "2rem",
        }}
      >
        <OutlinedButton
          colors={colors}
          ref={anchorRef}
          onClick={handleClick}
          disabled={
            (Array.isArray(sites) && sites.length === 0) ||
            isDashboardDataFetching ||
            isDashboardDataLoading ||
            isDashboardDataRefetching
          }
          endIcon={
            open ? (
              <KeyboardArrowUpRoundedIcon />
            ) : (
              <KeyboardArrowDownRoundedIcon />
            )
          }
        >
          {selectedSite !== "" && selectedGroup !== "" ? (
            <BodySmall>
              {selectedSite} - {selectedGroup}
            </BodySmall>
          ) : selectedSite !== "" && selectedGroup === "" ? (
            <> {selectedSite}</>
          ) : (
            <BodySmall>Select site or group</BodySmall>
          )}
        </OutlinedButton>
      </Box>

      <GroupOperationsCard
        open={open}
        handleClose={handleClose}
        anchorRef={anchorRef}
        selectedSite={selectedSite}
        selectedGroup={selectedGroup}
        setSelectedSite={setSelectedSite}
        setSelectedGroup={setSelectedGroup}
        user={user}
      />
    </React.Fragment>
  );
};
